import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertColor,
  Box,
  CardContent,
  LinearProgress,
  Typography,
} from "@mui/material";
import ConfirmationModal from "components/modals/confirmation-modal";
import { TooltipWrapper } from "components/permissions/permissions";
import { IdentityState, User, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { RejectedInvite, UserMembership } from "data/models";
import { userAcceptInvitation, userRejectInvitation } from "data/mutations";
import { fetchUserMemberships } from "data/queries";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { setCompletedWizardFlag } from "./onboarding-wizard";

interface Props {
  userState: User;
  setUserState: React.Dispatch<React.SetStateAction<IdentityState>>;
  setHasHandledInvitations: React.Dispatch<React.SetStateAction<boolean>>;
  children?: React.ReactNode;
}

interface Message {
  body: string;
  severity: AlertColor;
}

export const UserInvitationsOnBoarding = (props: Props) => {
  const [identity, setIdentity] = useIdentity();
  const user = identity as User;

  // Modal confirmation rejected
  const handleDeleteClose = () => {
    setDeleteOpen(false);
  };

  const initialUserInvitation: UserMembership = {
    organisationId: "",
    userMemberId: "",
    status: "",
    organisationName: "",
    userFullName: "",
    userEmail: "",
    organisationEmail: "",
    userFirstName: "",
    userLastName: "",
  };

  const [invitation, setInvitation] = useState<UserMembership>(
    initialUserInvitation
  );

  const handleDelete = (invitation: UserMembership) => {
    setInvitation(invitation);
    setOrganisationName(invitation.organisationName);

    setDeleteOpen(true);
  };
  const [organisationName, setOrganisationName] = useState("");

  // Delete Modal State
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [messages, setMessages] = useState<Message | null>(null);

  const queryClient = useQueryClient();
  const membershipsQuery = useQuery({
      queryKey: [QueryKeys.userMembership, user.cognitoSub],
      queryFn: () => fetchUserMemberships(true),
    });

  const acceptInvitationMutation = useMutation({
      mutationFn: (organisationId: string) => userAcceptInvitation(organisationId),
      onSuccess: async (newMembership) => {
          setMessages({
            severity: "success",
            body: `Invitation from "${newMembership.organisationName}" has been accepted`,
          });
          await setCompletedWizardFlag(user.cognitoUser);

          // Update the membership query cache first
          let pendingCount = 0;
          queryClient.setQueryData<UserMembership[]>(
            [QueryKeys.userMembership, user.cognitoSub],
            (oldMemberships) => {
              if (!oldMemberships) {
                return [newMembership];
              }

              const newState = oldMemberships.map((membership) => {
                if (membership.organisationId === newMembership.organisationId) {
                  return newMembership;
                }
                return membership;
              });

              pendingCount = newState.filter(
                (x) => x.status === "Pending"
              ).length;
              props.setHasHandledInvitations(pendingCount === 0);

              return newState;
            }
          );

          // Update user state with recalculated values
          props.setUserState((prevUserState) => {
            const user = prevUserState as User;

            // Add the newly accepted organization
            const updatedOrganisations = [
              ...user.organisations.map((x) => {
                return { ...x, active: false };
              }),
              {
                name: newMembership.organisationName,
                organisationId: newMembership.organisationId,
                active: false,
                hasAccess: newMembership.hasAccess, // Preserve access status from backend
              },
            ];

            // Set first accessible org as active
            const firstAccessibleIndex = updatedOrganisations.findIndex(
              (o) => o.hasAccess !== false
            );
            if (firstAccessibleIndex >= 0) {
              updatedOrganisations[firstAccessibleIndex].active = true;
            }

            // Recalculate accessibleCount based on actual access
            const accessibleCount = updatedOrganisations.filter(
              (o) => o.hasAccess !== false
            ).length;

            return {
              ...user,
              organisations: updatedOrganisations,
              onboarding: updatedOrganisations.length === 0, // Should be false since we just added one
              accessibleCount: accessibleCount > 0,
              hasPendingInvitations: pendingCount > 0,
            };
          });
        },
    });

  const rejectInvitationMutation = useMutation({
      mutationFn: (organisationId: string) => userRejectInvitation(organisationId),
      onSuccess: (rejectedMembership: RejectedInvite) => {
          setMessages({
            severity: "info",
            body: `Invitation from "${invitation.organisationName}" has been rejected`,
          });
          handleDeleteClose();

          // Update membership cache and get pending count
          let pendingCount = 0;
          queryClient.setQueryData<UserMembership[]>(
            [QueryKeys.userMembership, user.cognitoSub],
            (oldMemberships) => {
              if (!oldMemberships) {
                return [];
              }

              const newState = oldMemberships.filter(
                (membership) =>
                  membership.organisationId !== rejectedMembership.organisationId
              );

              pendingCount = newState.filter(
                (x) => x.status === "Pending"
              ).length;
              props.setHasHandledInvitations(pendingCount === 0);

              return newState;
            }
          );

          // Update user state with new pending count
          props.setUserState((prevUserState) => {
            return {
              ...(prevUserState as User),
              hasPendingInvitations: pendingCount > 0,
            };
          });
        },
    });

  const rows = !membershipsQuery.isSuccess
    ? []
    : membershipsQuery.data
        .filter((invitation) => invitation.status === "Pending")
        .map((invitation): any => {
          return {
            id: invitation.organisationName,
            invitation: invitation,
            organisationName: invitation.organisationName,
            actions: invitation.status,
          };
        })
        .sort((a, b) => {
          const nameA = a.organisationName.toUpperCase(); // ignore upper and lowercase
          const nameB = b.organisationName.toUpperCase(); // ignore upper and lowercase
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          // names must be equal
          return 0;
        });

  const invitations = rows;

  return (
    <>
      {messages && (
        <Box>
          <Alert variant="filled" severity={messages.severity}>
            {messages.body}
          </Alert>
        </Box>
      )}

      <ConfirmationModal
        isLoading={membershipsQuery.isPending}
        isError={membershipsQuery.isError}
        open={deleteOpen}
        header={`Reject this invitation?`}
        body={`You won't be able to join "${organisationName}" again unless you receive another invitation.`}
        leftButtonText={"CANCEL"}
        leftButtonColor="primary"
        rightButtonText={"REJECT INVITATION"}
        rightButtonColor="error"
        handleClose={() => handleDeleteClose()}
        handleLeftButton={() => handleDeleteClose()}
        isRightButtonLoading={rejectInvitationMutation.isPending}
        handleRightButton={() => {
          rejectInvitationMutation.mutate(invitation.organisationId);
        }}
      ></ConfirmationModal>

      <CardContent
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "column",
          gap: 2,
          padding: "0px !important",
        }}
      >
        <Typography variant="body1">
          {!membershipsQuery.isPending && rows.length == 0
            ? "No pending invitations"
            : "Pending organisation invitations"}
        </Typography>
        {props.children}
        {invitations.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {membershipsQuery.isPending ? (
              <LinearProgress />
            ) : (
              invitations.map((item) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TooltipWrapper
                    title={item.organisationName}
                    style={{ width: "50%", textAlign: "left" }}
                  >
                    <Typography noWrap>{item.organisationName}</Typography>
                  </TooltipWrapper>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                    }}
                  >
                    <LoadingButton
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={() =>
                        acceptInvitationMutation.mutate(
                          item.invitation.organisationId
                        )
                      }
                      loading={acceptInvitationMutation.isPending}
                    >
                      Accept
                    </LoadingButton>
                    <LoadingButton
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleDelete(item.invitation)}
                      loading={rejectInvitationMutation.isPending}
                    >
                      Reject
                    </LoadingButton>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}
      </CardContent>
    </>
  );
};
